import { useState } from "react";
import { ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface ImageGalleryProps {
  images: string[];
  serviceName: string;
}

const ImagePlaceholder = ({ className }: { className?: string }) => (
  <div className={cn("bg-muted relative overflow-hidden", className)}>
    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="rounded-full bg-muted-foreground/10 p-4">
        <ImageIcon className="w-8 h-8 text-muted-foreground/30" />
      </div>
    </div>
  </div>
);

const getGridConfig = (count: number) => {
  switch (count) {
    case 1:
      return {
        gridClass: "grid-cols-1 grid-rows-1",
        itemClasses: ["col-span-1 row-span-1"]
      };
    case 2:
      return {
        gridClass: "grid-cols-2 grid-rows-1",
        itemClasses: ["col-span-1 row-span-1", "col-span-1 row-span-1"]
      };
    case 3:
      return {
        gridClass: "grid-cols-4 grid-rows-2",
        itemClasses: ["col-span-2 row-span-2", "col-span-2 row-span-1", "col-span-2 row-span-1"]
      };
    case 4:
      return {
        gridClass: "grid-cols-4 grid-rows-2",
        itemClasses: [
          "col-span-2 row-span-2",
          "col-span-2 row-span-1",
          "col-span-1 row-span-1",
          "col-span-1 row-span-1"
        ]
      };
    case 5:
    default:
      return {
        gridClass: "grid-cols-4 grid-rows-2",
        itemClasses: [
          "col-span-2 row-span-2",
          "col-span-1 row-span-1",
          "col-span-1 row-span-1",
          "col-span-1 row-span-1",
          "col-span-1 row-span-1"
        ]
      };
  }
};

const ImageGallery = ({ images, serviceName }: ImageGalleryProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasImages = images && images.length > 0;
  const renderItems = hasImages ? images : Array(5).fill(null);
  const count = renderItems.length;
  const { gridClass, itemClasses } = getGridConfig(count);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % count);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? count - 1 : prev - 1));
  };

  return (
    <>
      {/* Airbnb-style Bento Grid */}
      <div 
        className="relative cursor-pointer group"
        onClick={() => setIsOpen(true)}
      >
        <div className={cn("grid gap-2 rounded-xl overflow-hidden h-[280px] sm:h-[340px] md:h-[400px]", gridClass)}>
          {renderItems.map((img, idx) => (
            <div key={idx} className={cn("relative", itemClasses[idx])}>
              {img ? (
                <img 
                  src={img} 
                  alt={`${serviceName} - ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImagePlaceholder className="w-full h-full" />
              )}
            </div>
          ))}
        </div>

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-xl pointer-events-none" />

        {/* Show all photos button */}
        <Button
          variant="secondary"
          size="sm"
          className="absolute bottom-4 right-4 font-medium shadow-md"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
        >
          Show all photos
        </Button>
      </div>

      {/* Fullscreen Gallery Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[100vw] w-full h-[100vh] p-0 border-0">
          <VisuallyHidden>
            <DialogTitle>Photo gallery for {serviceName}</DialogTitle>
          </VisuallyHidden>
          
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 left-4 z-50 text-foreground hover:bg-muted"
            onClick={() => setIsOpen(false)}
          >
            <X className="w-6 h-6" />
          </Button>

          {/* Image counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 text-foreground text-sm font-medium">
            {currentIndex + 1} / {count}
          </div>

          {/* Main image */}
          <div className="flex items-center justify-center h-full px-16">
            {renderItems[currentIndex] ? (
              <img
                src={renderItems[currentIndex]}
                alt={`${serviceName} - ${currentIndex + 1}`}
                className="max-h-[85vh] max-w-full object-contain"
              />
            ) : (
              <div className="w-full max-w-3xl aspect-video">
                <ImagePlaceholder className="w-full h-full rounded-lg" />
              </div>
            )}
          </div>

          {/* Navigation arrows */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-foreground hover:bg-muted h-12 w-12"
            onClick={goToPrev}
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-foreground hover:bg-muted h-12 w-12"
            onClick={goToNext}
          >
            <ChevronRight className="w-8 h-8" />
          </Button>

          {/* Thumbnail strip */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-50 overflow-x-auto max-w-full px-4 pb-2">
            {renderItems.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={cn(
                  "w-16 h-12 rounded overflow-hidden border-2 transition-all shrink-0",
                  currentIndex === idx ? "border-foreground" : "border-transparent opacity-60 hover:opacity-100"
                )}
              >
                {img ? (
                  <img
                    src={img}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-muted-foreground/20 flex items-center justify-center">
                    <ImageIcon className="w-4 h-4 text-muted-foreground/40" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageGallery;
